/* eslint-disable import/no-extraneous-dependencies */
import React from "react";
import Cta from "@qlik/qlik/src/components/Cta";
import Media from "@qlik/qlik/src/components/Media";
import { getCardBodyContent, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import * as styles from "./styles.module.css";

/**
 * HorizontalCTADocument is a component that displays a horizontal call to action with an image.
 * Renders a single card with an icon, title, description, subtitle, and cta links.
 * @param {Object} props - The component props.
 * @param {string} props.cardItem.title - The title of the card.
 * @param {Object} props.cardItem.body - The description for the card.
 * @param {Object} props.cardItem.eyebrowText - The eyebrowText for the card.
 * @param {Object} props.cardItem.media - The media for the card.
 * @param {Object[]} props.cardItem.cta - The cta links for the card.
 * @param {Array} props.backgroundImage - The background image of the card.
 * @param {Object} props.additionalConfiguration - The additional configuration of the card.
 * @returns {JSX.Element} - The rendered component.
 */
const HorizontalCTADocument = ({ data }) => {
    const { cardItem, backgroundImage = [], additionalConfiguration, customId } = data ?? {};
    const { title, cta, media, eyebrowText } = cardItem ?? {};

    // Set default alt text for background image
    const backgroundImageAlt = title ? `${title} Background Image` : "Background Image";

    const bodyContent = getCardBodyContent(cardItem);

    // Getting the spacing class name based on the additional configuration.
    const sectionPaddingClassName = getSpacingClassName(additionalConfiguration, "");

    return (
        // The main section of the component. It has a background color of ocean and a padding on the y-axis.
        <section
            className={`flex relative overflow-hidden lg:items-center bg-ocean pt-15 md:py-17 ${sectionPaddingClassName}`}
            id={customId}
        >
            <div className="container">
                <div className="row gap-y-12.5 md:flex-row-reverse md:items-center">
                    <div className="col-12 md:col-7 relative z-2 text-center md:text-left">
                        <div className="">
                            {eyebrowText && (
                                <p className="text-white text-sm lg:text-base leading-none tracking-wide4 lg:tracking-wide5 font-medium mb-3.75 md:mb-5">
                                    {eyebrowText}
                                </p>
                            )}
                            {/* eslint-disable-next-line react/no-danger */}
                            {title && (
                                <h2
                                    className="text-white"
                                    dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                                />
                            )}
                            {bodyContent && <div className={styles.description}>{bodyContent}</div>}
                            {cta && cta?.length > 0 && (
                                <div className="mt-7.5 lg:mt-15">
                                    {cta?.map((button) => (
                                        <div className="flex gap-2 justify-center md:justify-start" key={button.id}>
                                            <Cta key={button.id} data={button} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-12 md:col-5 relative z-1">
                        {backgroundImage?.length > 0 && backgroundImage?.[0]?.resource_type === "image" && (
                            <div className="absolute -z-1 -left-[550px] xl:-left-[900px] top-0.75 md:top-10 xl:top-21 translate-x-20 md:translate-x-0 lg:translate-x-10 xl:translate-x-50 lg:top-[87px]">
                                <Media
                                    link={backgroundImage[0]?.secure_url}
                                    width={backgroundImage[0].width}
                                    height={backgroundImage[0].height}
                                    alt={backgroundImageAlt}
                                />
                            </div>
                        )}
                        {media?.[0] && (
                            <div className="flex justify-center lg:justify-start">
                                <Media
                                    media={media?.[0]}
                                    className="w-[207px] lg:w-65.25 h-full xl:w-82.5"
                                    videoClassName="w-[207px] lg:w-65.25 h-full xl:w-82.5"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HorizontalCTADocument;
